/**
 * Aanwezigheid van chatters: elke minuut een heartbeat, en "offline" bij
 * uitloggen (30-09-2026, Jordy).
 *
 * Waarom: het nieuwe dashboard riep `/chatter-finance/heartbeat` nergens aan.
 * Sinds de overstap kwamen er daardoor geen heartbeats meer bij (alleen nog via
 * de sync van oud), en daar draaien de online-indicator en de werktijd per
 * chatter op. Het oude dashboard stuurde er één per 60 s zolang het tabblad
 * zichtbaar was; dit doet hetzelfde.
 *
 * Alleen voor accounts die aan een chatter gekoppeld zijn (de server negeert
 * de rest toch), en maar één heartbeat per minuut, ook met vijf tabbladen open.
 */
import { useEffect } from 'react'
import { getToken } from './api'

export const HARTSLAG_MS = 60_000
/** Gedeeld tussen tabbladen: wanneer is de laatste heartbeat verstuurd. */
const SLEUTEL = 'novahub_hartslag_op'
/** Een ander tabblad dat binnen deze tijd al stuurde, telt voor deze minuut. */
const MARGE_MS = 50_000

function laatsteHartslag(): number {
  try {
    return Number(localStorage.getItem(SLEUTEL) || 0)
  } catch {
    return 0
  }
}

function markeer(nu: number): void {
  try {
    localStorage.setItem(SLEUTEL, String(nu))
  } catch {
    /* private mode: dan telt alleen dit tabblad */
  }
}

async function stuur(pad: string, body: unknown, keepalive = false): Promise<void> {
  const token = getToken()
  if (!token) return
  try {
    await fetch(pad, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    })
  } catch {
    /* een gemiste heartbeat is geen fout voor de gebruiker */
  }
}

/** Eén heartbeat, als het tabblad zichtbaar is en geen ander tabblad net stuurde. */
export async function hartslag(nu: number = Date.now()): Promise<boolean> {
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return false
  const laatste = laatsteHartslag()
  // Een tijdstempel in de toekomst (klok teruggezet) telt niet: anders zou de
  // heartbeat wachten tot die tijd voorbij is.
  if (laatste <= nu + 5_000 && nu - laatste < MARGE_MS) return false
  markeer(nu)
  await stuur('/api/admin/chatter-finance/heartbeat', { page: window.location.pathname })
  return true
}

/** Elke minuut een heartbeat zolang `aan` (= ingelogd én gekoppeld aan een chatter). */
export function useHartslag(aan: boolean): void {
  useEffect(() => {
    if (!aan) return
    void hartslag()
    const iv = window.setInterval(() => { void hartslag() }, HARTSLAG_MS)
    const terug = () => { if (document.visibilityState === 'visible') void hartslag() }
    document.addEventListener('visibilitychange', terug)
    return () => {
      window.clearInterval(iv)
      document.removeEventListener('visibilitychange', terug)
    }
  }, [aan])
}

/**
 * Meld "offline" bij uitloggen, vóór het token wordt gewist. `keepalive`
 * zorgt dat het verzoek ook doorgaat als de pagina meteen herlaadt.
 */
export async function meldOffline(): Promise<void> {
  await stuur('/api/admin/chatter-finance/presence/offline', { reason: 'manual' }, true)
  try {
    localStorage.removeItem(SLEUTEL)
  } catch {
    /* niets te doen */
  }
}
